import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { GroupPreferenceApiService } from './group-preference-api.service';
import { StorageService } from './storage.service';

export interface PreferredGroupContext {
  id: number;
  name?: string;
  group_type?: string;
}

// Business logic for signup-group-preference-redirect: seeds a fan's preferred group at signup
// and resolves it back to a group_slug on later no-group-in-URL logins. Reuses the existing
// grouping-preferences save/fetch contract (see GroupPreferenceApiService) rather than new storage.
@Injectable({
  providedIn: 'root',
})
export class GroupPreferenceService {
  constructor(private groupPreferenceApiService: GroupPreferenceApiService, private storageService: StorageService) {}

  seedPreferredGroupOnSignup(
    group: PreferredGroupContext,
    userId: number | string,
    organizationId: number | string,
    channelName: string
  ): Observable<void> {
    if (!group?.id) {
      return of(void 0);
    }

    const fallback = { id: group.id, name: group.name || '', group_type: group.group_type || '' };

    // The performer-info group's group_type differs from the one the AI-agent hero panel's
    // grouping questions use, so saving it as-is made the panel synthesize a duplicate group
    // section. Save the panel's own option for this group instead, exactly as the panel would.
    return this.groupPreferenceApiService.fetchGroupingPreferences(userId, organizationId, channelName).pipe(
      catchError(() => of(null)),
      map((response: any) => this.findGroupQuestionOption(response, group.id) || fallback),
      switchMap((selectedGroup) =>
        this.groupPreferenceApiService.saveGroupPreference({ userId, organizationId, channelName, groups: [selectedGroup] })
      ),
      map(() => void 0),
      catchError(() => of(void 0))
    );
  }

  private findGroupQuestionOption(response: any, groupId: number): any | null {
    const questions: any[] = response?.all_preference_questions || response?.preference_questions || [];
    for (const question of questions) {
      if (question?.type !== 'group') continue;
      const option = (question.options || []).find((o: any) => Number(o?.id) === Number(groupId));
      if (option) return option;
    }
    return null;
  }

  resolvePreferredGroupSlug(
    userId: number | string,
    organizationId: number | string,
    channelName: string,
    groups: any[]
  ): Observable<string | null> {
    if (!userId || !organizationId || !groups?.length) {
      return of(null);
    }

    return this.groupPreferenceApiService.fetchGroupingPreferences(userId, organizationId, channelName).pipe(
      map((response: any) => this.resolveSlugFromResponse(response, groups)),
      catchError(() => of(null))
    );
  }

  // Login-time version of the redirect. Claims this session's one redirect for the org (the same
  // flag ChannelWebpageGuard checks), otherwise the guard repeats it the first time the fan picks
  // the network from the group menu - "conference selection only works on the 2nd try".
  resolveLoginRedirectSlug(
    userId: number | string,
    organizationId: number | string,
    channelName: string,
    groups: any[]
  ): Observable<string | null> {
    this.storageService.markGroupRedirectOffered(organizationId);
    return this.resolvePreferredGroupSlug(userId, organizationId, channelName, groups);
  }

  private resolveSlugFromResponse(response: any, groups: any[]): string | null {
    const preferredIds: number[] = [];
    (response?.preferable_groups || []).forEach((groupObj: any) => {
      Object.keys(groupObj || {}).forEach((groupType) => {
        (groupObj[groupType] || []).forEach((entry: any) => {
          const id = Number(entry?.id);
          if (id) {
            preferredIds.push(id);
          }
        });
      });
    });

    const matchedGroup = groups.find((g: any) => preferredIds.includes(Number(g.id)));
    return matchedGroup?.group_slug || null;
  }
}
